import Form     from 'scripts/poppet/form';
import template from 'templates/logEntryCreateForm.jst.html';

/**
 * defa.views.LogEntryCreateForm
 * A form to create a new log entry.
 * @version 1.2.0
 */

const channelUser = Bb.Radio.channel( 'user' );
const channel     = Bb.Radio.channel( 'objects' );

const selectors = {
	inputUnit           : '[data-input=entry-unit]',
	inputCustomer       : '[data-input=entry-customer]',
	inputContent        : '[data-input=entry-content]',
	outputRemainingBytes: '[data-output=remaining-bytes]',
};

const LogEntryCreateForm = Form.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template        : template,
	noInitValidation: true,
	
	ui: {
		unit     : selectors.inputUnit,
		customer : selectors.inputCustomer,
		content  : selectors.inputContent,
		remaining: selectors.outputRemainingBytes,
	},
	
	events: {
		'input @ui.content': 'onInputContent',
	},
	
	templateContext: function () {
		return {
			context    : this.options.type,
			isUnit     : this.isUnit(),
			isCustomer : this.isCustomer(),
			hasCustomer: this.hasCustomer(),
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers whenever the user changes the content.
	 * @void
	 */
	onInputContent: function ( event ) {
		this._controlInputContent();
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Strips the textarea length and informs the user of the current byteCount.
	 * @void
	 */
	_controlInputContent: function () {
		let input           = this.ui.content.val().trim(),
			inputByteLength = this.getByteLength( input ),
			maxStringLength = 10007,
			remainingBytes  = maxStringLength - inputByteLength;
		
		// if the content is too long, trim it once and run again.
		if ( remainingBytes < 0 ) {
			
			this.ui.content.val( input.slice( 0, input.length - 1 ) );
			this._controlInputContent();
			
			// if thte content is allright, update the counter.
		} else {
			// show counter if under 1000 chars
			if ( remainingBytes > 1000 ) {
				this.ui.remaining.removeClass( 'show' );
			} else {
				this.ui.remaining.addClass( 'show' );
			}
			
			// show red counter under 100 chars
			if ( remainingBytes < 100 ) {
				this.ui.remaining.addClass( 'danger' );
			} else {
				this.ui.remaining.removeClass( 'danger' );
			}
			
			// update counter
			this.ui.remaining.html( remainingBytes );
		}
	},
	
	/**
	 * Returns the byte length of an utf-8 string.
	 * https://stackoverflow.com/a/23329386
	 * @return {Number}  The length of bytes of the input string.
	 */
	getByteLength: function ( str ) {
		
		// NB:
		// When we leave IE, and Edge catches up:
		// ( new TextEncoder( "utf-8" ).encode( "foo" ) ).length
		
		let s = str.length;
		
		for ( let i = str.length - 1; i >= 0; i-- ) {
			let code = str.charCodeAt( i );
			if ( code > 0x7f && code <= 0x7ff ) {
				s++;
			} else if ( code > 0x7ff && code <= 0xffff ) {
				s += 2;
			}
			
			if ( code >= 0xDC00 && code <= 0xDFFF ) {
				i--; //trail surrogate
			}
		}
		
		return s;
	},
	
	/**
	 * Returns true if this is a customer log.
	 * @void
	 */
	isCustomer: function () {
		return ( 'billing' === this.options.type );
	},
	
	/**
	 * Returns true if this is a unit log.
	 * @void
	 */
	isUnit: function () {
		return ( 'device' === this.options.type );
	},
	
	/**
	 * Checks if the current unit has a customer.
	 * @void
	 */
	hasCustomer: function () {
		if ( 'device' === this.options.type ) {
			let unit         = channel.request( 'units' ).get( this.options.bid ),
				subscription = channel.request( 'subscriptions' ).get( unit.get( 'transientSubscription' ) );
			
			return ( subscription );
		} else {
			return true;
		}
	},
	
	/**
	 * Gets the respective unit's id.
	 * @return {int}  The current log page's unit id, false if this isn't a unit's log.
	 */
	getUnitId: function () {
		return ( this.isUnit() )
			   ? parseInt( this.options.bid )
			   : false;
	},
	
	/**
	 * Gets the respective customer's id.
	 * @return {int}  The current log page's customer id, or the current unit's customer id.
	 */
	getCustomerId: function () {
		let customerId;
		
		if ( this.isCustomer() ) {
			customerId = this.options.bid;
		}
		
		if ( this.isUnit() && this.hasCustomer() ) {
			let unit         = channel.request( 'units' ).get( this.options.bid ),
				subscription = channel.request( 'subscriptions' ).get( unit.get( 'transientSubscription' ) );
			
			customerId = subscription.get( 'owner' );
		}
		
		return ( customerId )
			   ? parseInt( customerId )
			   : null;
	},
	
	/**
	 * Saves the form data to the new LogEntry model.
	 * @void
	 */
	submit: function () {
		
		let logEntry = {
			content         : this.ui.content.val().trim(),
			transientChangee: channelUser.request( 'id' ),
		};
		
		if ( this.isUnit() && this.ui.unit.is( ':checked' ) ) {
			logEntry.unit = this.getUnitId();
		}
		
		if ( this.isCustomer() || this.ui.customer.is( ':checked' ) ) {
			logEntry.customer = this.getCustomerId();
		}
		
		this.model.save( logEntry );
	},
	
} );

export default LogEntryCreateForm;